// "Start from a template" (collaboration v1, PR 6 §4; PL-14): the curated
// commerce templates on the start screen (while no Skill is published), on
// Skills and on Agents. One card everywhere (DESIGN-SPEC §6.5): the whole card
// is its one action, Use template, which creates an ordinary private Skill
// draft (on Agents: a private agent, switched off, from the template's instructions); what a template needs is muted meta, and setting it up is behind ⋯.
//
// The views are pure (TemplateGalleryFrame, TemplateCardView), so the layout
// can be measured in a real browser with the real markup
// (test/ui-template-gallery-geometry-browser.mjs).
import { useEffect, useId, useState } from 'react';
import type { ReactNode } from 'react';
import { CableIcon, LoaderCircleIcon } from 'lucide-react';
import { OverflowMenu } from '@/components/ui/menu';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import { overlaysCopy } from '@/i18n/ns/overlays';
import { templatesCopy } from '@/i18n/ns/templates';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import { resolveRequestedAppLocale } from '@/i18n/eu-locales';
import { useWorkspace } from './context';
import { WorkspaceIcon } from './icons';
import { agentsApi, announceAgentsChanged } from './agents/api';
import { livingApi } from './living-api';
import type { GalleryLocale, GalleryTemplate } from './living-api';

/** What "Use template" makes: a Skill draft (Skills, Home) or an agent (Agents). */
export type TemplateTarget = 'skill' | 'agent';

function useGalleryLanguage() {
  return resolveRequestedAppLocale(useChatInterfaceLocale(), null) as GalleryLocale;
}

/** "Fortnox and Gmail" in the interface language; a plain comma list where ICU has none. */
export function joinAppNames(names: string[], locale: string) {
  try {
    return new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(names);
  } catch {
    return names.join(', ');
  }
}

type MetaStatus = { tone: 'busy' | 'missing' | 'ready'; text: string };

/** What the card's meta line says on the left: busy, what is missing, or what it uses. */
function useTemplateStatus(template: GalleryTemplate, busy: boolean): MetaStatus | null {
  const copy = useNamespaceCopy(templatesCopy);
  const locale = useChatInterfaceLocale();
  if (busy) return { tone: 'busy', text: copy.creating };
  const missing = template.needs.filter((need) => !need.connected).map((need) => need.label);
  if (missing.length)
    return {
      tone: 'missing',
      text: formatCopy(copy.needs, { apps: joinAppNames(missing, locale) }),
    };
  if (!template.needs.length) return null;
  const apps = joinAppNames(
    template.needs.map((need) => need.label),
    locale,
  );
  return { tone: 'ready', text: formatCopy(copy.uses, { apps }) };
}

const STATUS_ICONS = { missing: 'connections', ready: 'check' } as const;

/**
 * The card's one meta line: the status on the left and the suggested schedule
 * on the right. A narrow card stacks them (living.css), keeping the status on
 * the bottom line so it stays aligned across the row.
 */
function TemplateMeta({
  template,
  busy,
  target,
}: {
  template: GalleryTemplate;
  busy: boolean;
  target: TemplateTarget;
}) {
  const copy = useNamespaceCopy(templatesCopy);
  const status = useTemplateStatus(template, busy);
  return (
    <div className="ws-gallery-meta">
      {status && (
        <span className="ws-gallery-status" data-tone={status.tone} title={status.text}>
          {status.tone === 'busy' ? (
            <LoaderCircleIcon className="ws-gallery-spinner" aria-hidden="true" />
          ) : (
            <WorkspaceIcon name={STATUS_ICONS[status.tone]} size={12} />
          )}
          <span>{status.text}</span>
        </span>
      )}
      {template.suggestedSchedule && (
        <span className="ws-gallery-schedule">
          <WorkspaceIcon name="clock" size={12} />
          {target === 'agent'
            ? `${template.suggestedSchedule.label} · ${copy.scheduleStartsWhenOn}`
            : template.suggestedSchedule.label}
        </span>
      )}
    </div>
  );
}

/** One template card: pure, so the geometry suite renders exactly this markup. */
export function TemplateCardView({
  template,
  busy = false,
  target = 'skill',
  onUse,
  onSetUp,
}: {
  template: GalleryTemplate;
  busy?: boolean;
  target?: TemplateTarget;
  onUse: () => void;
  onSetUp: () => void;
}) {
  const copy = useNamespaceCopy(templatesCopy);
  const overlays = useNamespaceCopy(overlaysCopy);
  const descriptionId = useId();
  return (
    <article
      className="ws-gallery-card"
      data-busy={busy || undefined}
      aria-busy={busy || undefined}
      onClick={(event) => {
        // Native clicks on the stretched pseudo-element may target the card.
        // The named button owns keyboard activation; secondary controls stay separate.
        if (
          busy ||
          !(event.target instanceof Element) ||
          event.target.closest('button, [role="menuitem"]')
        )
          return;
        onUse();
      }}
    >
      <div className="ws-gallery-card-top">
        <span className="ws-gallery-tile" aria-hidden="true">
          <WorkspaceIcon name={target === 'agent' ? 'agents' : 'skills'} size={16} />
        </span>
        {!template.ready && (
          <OverflowMenu
            className="ws-gallery-more"
            label={formatCopy(overlays.moreActionsFor, { name: template.name })}
            items={[
              {
                key: 'connect',
                label: overlays.setUpConnections,
                icon: <CableIcon aria-hidden="true" />,
                onSelect: onSetUp,
              },
            ]}
          />
        )}
      </div>
      <h3 className="ws-gallery-name">
        {/* The card's one target: stretched over the whole card by CSS. */}
        <button
          type="button"
          className="ws-gallery-use"
          aria-label={formatCopy(copy.useTemplate, { name: template.name })}
          aria-describedby={descriptionId}
          aria-disabled={busy || undefined}
          title={template.name}
          onClick={() => {
            if (!busy) onUse();
          }}
        >
          <span className="ws-gallery-name-text">{template.name}</span>
          <WorkspaceIcon name="arrow" size={14} className="ws-gallery-go" />
        </button>
      </h3>
      <p className="ws-gallery-description" id={descriptionId}>
        {template.description}
      </p>
      <TemplateMeta template={template} busy={busy} target={target} />
    </article>
  );
}

/** The gallery's section: one header treatment on every page, then the grid. A place with its own starting points
 * (the Apps ideas) names them itself. */
export function TemplateGalleryFrame({
  children,
  target = 'skill',
  heading,
  intro,
  className,
}: {
  children: ReactNode;
  target?: TemplateTarget;
  heading?: string;
  intro?: string;
  className?: string;
}) {
  const copy = useNamespaceCopy(templatesCopy);
  const headingId = useId();
  return (
    <section
      className={`ws-template-gallery${className ? ` ${className}` : ''}`}
      aria-labelledby={headingId}
    >
      <header className="ws-gallery-header">
        <h2 className="ws-gallery-heading" id={headingId}>
          {heading ?? copy.heading}
        </h2>
        <p className="ws-gallery-intro">
          {intro ?? (target === 'agent' ? copy.agentDescription : copy.description)}
        </p>
      </header>
      <div className="ws-gallery">{children}</div>
    </section>
  );
}

function useTemplateAction(template: GalleryTemplate, target: TemplateTarget) {
  const ws = useWorkspace();
  const copy = useNamespaceCopy(templatesCopy);
  const [busy, setBusy] = useState(false);
  const use = async () => {
    if (busy) return;
    const ownsNavigation = ws.beginNavigation();
    setBusy(true);
    try {
      if (target === 'agent') {
        // A personal agent, switched off, from the template's own instructions. No Skill is
        // created or attached (an agent can only use a published one) and nothing is published.
        const { draftInput } = template;
        const agent = await agentsApi.create({
          name: draftInput.name,
          description: draftInput.description,
          scope: 'personal',
          goal: template.description,
          instructions: draftInput.instructions,
          expectedOutput: '',
          boundaries: '',
          resources: '',
          skillIds: [],
          emailOnFinish: false,
          resultMode: 'new',
          // The suggested schedule is kept on the agent but only runs once it is turned on.
          schedule: template.suggestedSchedule
            ? {
                cron: template.suggestedSchedule.cron,
                timezone: template.suggestedSchedule.timezone,
              }
            : null,
          enabled: false,
        });
        announceAgentsChanged();
        if (!ownsNavigation()) return;
        ws.openCanvas({ kind: 'agent', id: agent.id });
        ws.notify(formatCopy(copy.agentDraftCreated, { name: agent.name }), null, 'success');
        return;
      }
      // An ordinary private Skill draft: change it in chat, then publish.
      const { skill } = await livingApi.useTemplate(template);
      if (!ownsNavigation()) return;
      await ws.refresh();
      if (!ownsNavigation()) return;
      ws.openCanvas({ kind: 'skill', id: skill.id });
      ws.notify(
        formatCopy(copy.draftCreated, { name: skill.draft?.name ?? template.name }),
        null,
        'success',
      );
    } catch {
      if (ownsNavigation())
        ws.notify(target === 'agent' ? copy.agentDraftFailed : copy.draftFailed, null, 'error');
    } finally {
      setBusy(false);
    }
  };
  return { busy, use };
}

function TemplateCard({ template, target }: { template: GalleryTemplate; target: TemplateTarget }) {
  const ws = useWorkspace();
  const { busy, use } = useTemplateAction(template, target);
  return (
    <TemplateCardView
      template={template}
      busy={busy}
      target={target}
      onUse={() => void use()}
      onSetUp={() => ws.navigate('connections')}
    />
  );
}

/** The commerce templates, marked with what each needs. */
export function TemplateGallery({
  limit,
  target = 'skill',
}: {
  limit?: number;
  target?: TemplateTarget;
}) {
  const language = useGalleryLanguage();
  const signedInUserId = useWorkspace().user?.id;
  const [templates, setTemplates] = useState<GalleryTemplate[] | null>(null);
  useEffect(() => {
    // Only for a committed account (see useCompanyFeed): it mounts while the session is unresolved.
    if (!signedInUserId) {
      setTemplates(null);
      return;
    }
    let active = true;
    livingApi
      .gallery(language)
      .then((value) => {
        if (active) setTemplates(value.templates);
      })
      .catch(() => {
        if (active) setTemplates([]);
      });
    return () => {
      active = false;
    };
  }, [language, signedInUserId]);
  if (!templates?.length) return null;
  return (
    <TemplateGalleryFrame target={target}>
      {templates.slice(0, limit ?? templates.length).map((template) => (
        <TemplateCard key={template.id} template={template} target={target} />
      ))}
    </TemplateGalleryFrame>
  );
}
